import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProperty } from '../context/PropertyContext';
import PropertyCard from '../components/PropertyCard';
import SearchForm from '../components/SearchForm';
import CategorySection from '../components/CategorySection';
import HeroSlider from '../components/HeroSlider';
import TypewriterEffect from '../components/TypewriterEffect';
import ComponentErrorBoundary from '../components/ComponentErrorBoundary';
import { Search, Star, TrendingUp, Shield } from 'lucide-react';

const HomePage: React.FC = () => {
  console.log('🏠 HomePage: Inicializando...');
  
  const { filteredProperties, searchFilters } = useProperty();
  const navigate = useNavigate();
  
  console.log('🏠 HomePage: Propriedades filtradas recebidas:', filteredProperties?.length || 0);
  console.log('🏠 HomePage: Filtros ativos:', searchFilters);
  
  // Verificar se filteredProperties existe e é um array antes de fazer operações
  const safeProperties = Array.isArray(filteredProperties) ? filteredProperties : [];
  
  // Se há filtro de transação ativo, usar apenas as propriedades filtradas
  // Senão, mostrar seções separadas de venda e locação
  let saleProperties: any[] = [];
  let rentalProperties: any[] = [];
  
  if (searchFilters.transactionType === 'Venda') {
    saleProperties = safeProperties.slice(0, 12); // Mostrar mais quando filtrado
    rentalProperties = [];
  } else if (searchFilters.transactionType === 'Aluguel') {
    saleProperties = [];
    rentalProperties = safeProperties.slice(0, 12); // Mostrar mais quando filtrado
  } else {
    // Sem filtro específico - mostrar seções separadas
    saleProperties = safeProperties
      .filter(p => p?.transactionType === 'Venda' || p?.transactionType === 'Venda/Aluguel')
      .slice(0, 6);
    
    rentalProperties = safeProperties
      .filter(p => p?.transactionType === 'Aluguel' || p?.transactionType === 'Venda/Aluguel')
      .slice(0, 6);
  }

  console.log('🏠 HomePage: Imóveis para venda:', saleProperties.length);
  console.log('🏠 HomePage: Imóveis para locação:', rentalProperties.length);

  const typewriterTexts = [
    'Imóvel dos Sonhos',
    'Primeiro Imóvel', 
    'Grande Investimento',
    'Lar Doce Lar'
  ];

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="text-white min-h-screen flex items-center relative -mt-20 pt-20 overflow-hidden">
        <ComponentErrorBoundary componentName="HeroSlider">
          <HeroSlider />
        </ComponentErrorBoundary>
        
        <div className="w-full">
          <div className="text-center relative z-10 mb-8">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Encontre seu
              <span className="block text-orange-200">
                <TypewriterEffect texts={typewriterTexts} />
              </span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-orange-100">
              Descubra as melhores oportunidades imobiliárias com a confiança 
              que você merece
            </p>
            <div className="w-full">
              <SearchForm compact onSearch={() => navigate('/busca')} />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-orange-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Busca Inteligente</h3>
              <p className="text-gray-600">
                Encontre imóveis com filtros avançados e resultados precisos
              </p>
            </div>
            <div className="text-center">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-orange-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Imóveis Selecionados</h3>
              <p className="text-gray-600">
                Apenas os melhores imóveis, cuidadosamente selecionados
              </p>
            </div>
            <div className="text-center">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-orange-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Segurança Total</h3>
              <p className="text-gray-600">
                Transações seguras e documentação completa
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <CategorySection />

      {/* Properties for Sale */}
      {(searchFilters.transactionType !== 'Aluguel' && saleProperties.length > 0) && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-2">
                  {searchFilters.transactionType === 'Venda' ? 'Imóveis à Venda' : 'Últimos Imóveis à Venda'}
                </h2>
                <p className="text-gray-600">
                  {searchFilters.transactionType === 'Venda' ? 'Imóveis encontrados para compra' : 'Os mais novos imóveis disponíveis para compra'}
                </p>
              </div>
              <Link 
                to="/busca?transactionType=Venda"
                className="text-orange-500 hover:text-orange-600 font-medium flex items-center space-x-1"
              >
                <span>Ver todos</span>
                <TrendingUp className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {saleProperties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Properties for Rent */}
      {(searchFilters.transactionType !== 'Venda' && rentalProperties.length > 0) && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-2">
                  {searchFilters.transactionType === 'Aluguel' ? 'Imóveis para Locação' : 'Últimos Imóveis para Locação'}
                </h2>
                <p className="text-gray-600">
                  {searchFilters.transactionType === 'Aluguel' ? 'Imóveis encontrados para aluguel' : 'Os mais novos imóveis disponíveis para aluguel'}
                </p>
              </div>
              <Link 
                to="/busca?transactionType=Aluguel"
                className="text-orange-500 hover:text-orange-600 font-medium flex items-center space-x-1"
              >
                <span>Ver todos</span>
                <TrendingUp className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rentalProperties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-orange-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Pronto para Encontrar seu Novo Lar?
          </h2>
          <p className="text-xl mb-8 text-orange-100">
            Explore nosso portfólio completo e encontre o imóvel perfeito para você
          </p>
          <Link 
            to="/busca"
            className="bg-white text-orange-500 px-8 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-colors inline-flex items-center space-x-2"
          >
            <Search className="w-5 h-5" />
            <span>Começar Busca</span>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;